import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, makeStyles } from '@material-ui/core';
import { showToast } from '@klpkonsern/ui';
import {
    ActionButton,
    Block,
    ConfirmationDialog,
    IconName,
    Illustration,
} from '@klp/design';
import type { Avfall } from '../services/rtkApi';
import {
    useAnalyzeProsjektAvfallMutation,
    useGetProsjektAvfallTypesQuery,
    useUpdateProsjektAvfallMutation,
    type ProsjektAvfall,
} from '../services/rtkApi';
import {
    ProsjektAvfallHeader,
    ProsjektAvfallDisplayRow,
    ProsjektAvfallEditRow,
    ProsjektAvfallSummary,
    ProsjektAvfallKlimagassField,
    calculateMengde,
    createEmptyAvfall,
    validateAvfall,
    validateKlimagassberegning,
    hasValidationErrors,
    clearFieldError,
    clearRowErrors,
    updateAvfallField,
    deleteAvfallRow,
    type ValidationErrors,
} from './ProsjektAvfall';

const useStyles = makeStyles((theme) => ({
    container: {
        padding: theme.spacing(2),
        justifyContent: 'center',
    },
    actionContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(1),
    },
    addButtonContainer: {
        marginTop: theme.spacing(2),
    },
}));

type ProsjektAvfallExpandableProps = {
    element: ProsjektAvfall;
};

export function ProsjektAvfallExpandable({
    element,
}: ProsjektAvfallExpandableProps) {
    const { t } = useTranslation();
    const classes = useStyles();
    const anchorRef = React.useRef<HTMLDivElement>(null);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editedAvfall, setEditedAvfall] = useState<Array<Avfall>>([]);
    const [editedKlimagass, setEditedKlimagass] = useState<number | null>(null);
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
        {},
    );

    const { data: avfallTypes } = useGetProsjektAvfallTypesQuery();
    const [analyzeProsjektAvfall, { isLoading }] =
        useAnalyzeProsjektAvfallMutation();
    const [updateProsjektAvfall, { isLoading: isUpdating }] =
        useUpdateProsjektAvfallMutation();

    // Compute totals from edited or original data
    const displayAvfall = isEditMode ? editedAvfall : element.avfall;
    const totalMengde = useMemo(
        () => calculateMengde(displayAvfall, undefined),
        [displayAvfall],
    );
    const totalMengdeSortert = useMemo(
        () => calculateMengde(displayAvfall, true),
        [displayAvfall],
    );
    const totalMengdeUsortert = useMemo(
        () => calculateMengde(displayAvfall, false),
        [displayAvfall],
    );

    const handleEditClick = () => {
        setEditedAvfall([...element.avfall]);
        setEditedKlimagass(element.klimagassberegning);
        setIsEditMode(true);
    };

    const handleCancelClick = () => {
        setEditedAvfall([]);
        setEditedKlimagass(null);
        setValidationErrors({});
        setIsEditMode(false);
    };

    const handleKlimagassChange = (value: number | null) => {
        setEditedKlimagass(value);
        setValidationErrors((prev) => {
            const next = { ...prev };
            delete next.klimagassberegning;
            return next;
        });
    };

    const handleAvfallChange = (
        index: number,
        field: 'avfallTypeId' | 'mengde',
        value: number | null,
    ) => {
        setEditedAvfall((prev) =>
            updateAvfallField(prev, index, field, value, avfallTypes),
        );
        setValidationErrors((prev) => clearFieldError(prev, index, field));
    };

    const handleAddRow = () => {
        setEditedAvfall((prev) => [...prev, createEmptyAvfall()]);
    };

    const handleDeleteRow = (index: number) => {
        setEditedAvfall((prev) => deleteAvfallRow(prev, index));
        setValidationErrors((prev) => clearRowErrors(prev, index));
    };

    const handleValidation = (): boolean => {
        const errors = {
            ...validateAvfall(editedAvfall),
            ...validateKlimagassberegning(editedKlimagass),
        };
        setValidationErrors(errors);
        return !hasValidationErrors(errors);
    };

    const handleSave = () => {
        if (!handleValidation()) {
            return;
        }

        updateProsjektAvfall({
            id: element.id!,
            avfall: editedAvfall,
            klimagassberegning: editedKlimagass,
        })
            .unwrap()
            .then(() => {
                showToast({
                    body: t('sustainability.prosjektAvfall.updateSuccess'),
                    type: 'success',
                });
                setIsEditMode(false);
                setEditedAvfall([]);
                setEditedKlimagass(null);
                setValidationErrors({});
            })
            .catch(() => {
                showToast({
                    body: t('general.error.message'),
                    type: 'error',
                });
            });
    };

    const handleAnalyze = () => {
        setShowConfirmationDialog(false);
        analyzeProsjektAvfall({
            prosessInstansId: element.prosessInstansId,
            saveResult: true,
        })
            .unwrap()
            .then(() => {
                showToast({
                    body: t(
                        'sustainability.prosjektAvfall.uploadRapportSuccess',
                    ),
                    type: 'success',
                });
            })
            .catch(() => {
                showToast({
                    body: t('general.error.message'),
                    type: 'error',
                });
            });
    };

    return (
        <Grid className={classes.container} container={true}>
            <Grid
                item={true}
                xs={12}
                lg={isEditMode ? 12 : 10}
                xl={isEditMode ? 12 : 8}
            >
                <Grid container={true} spacing={2}>
                    <Grid item={true} xs={12}>
                        {!isEditMode ? (
                            <Block
                                className={classes.actionContainer}
                                marginBottom="compact"
                            >
                                <ActionButton
                                    variant="contained"
                                    color="secondary"
                                    onClick={handleEditClick}
                                    actionType="button"
                                    endIcon={
                                        <Illustration icon={IconName.EDIT} />
                                    }
                                    translation="sustainability.prosjektAvfall.editAction"
                                />
                                <div ref={anchorRef}>
                                    <ActionButton
                                        isLoading={isLoading}
                                        variant="contained"
                                        color="secondary"
                                        onClick={() =>
                                            setShowConfirmationDialog(true)
                                        }
                                        actionType="button"
                                        endIcon={
                                            <Illustration
                                                icon={IconName.CHANGE}
                                            />
                                        }
                                        translation="sustainability.prosjektAvfall.analyzeAction"
                                    />
                                </div>
                            </Block>
                        ) : (
                            <Block
                                className={classes.actionContainer}
                                marginBottom="compact"
                            >
                                <ActionButton
                                    variant="outlined"
                                    color="primary"
                                    onClick={handleCancelClick}
                                    actionType="cancel"
                                    translation="general.cancel"
                                />
                                <ActionButton
                                    isLoading={isUpdating}
                                    variant="contained"
                                    color="secondary"
                                    onClick={handleSave}
                                    actionType="save"
                                    translation="general.save"
                                />
                            </Block>
                        )}
                    </Grid>
                    <Grid item={true} xs={12}>
                        <ProsjektAvfallKlimagassField
                            isEditMode={isEditMode}
                            value={
                                isEditMode
                                    ? editedKlimagass
                                    : element.klimagassberegning
                            }
                            error={validationErrors.klimagassberegning}
                            onChange={handleKlimagassChange}
                        />
                    </Grid>
                    <Grid item={true} xs={12}>
                        <ProsjektAvfallHeader
                            isEditMode={isEditMode}
                            showAvfallColumns={true}
                        />
                        {isEditMode
                            ? displayAvfall.map((avfall, index) => (
                                  <ProsjektAvfallEditRow
                                      key={`${avfall.id}-${index}`}
                                      index={index}
                                      avfall={avfall}
                                      avfallTypes={avfallTypes}
                                      totalMengde={totalMengde}
                                      validationErrors={validationErrors}
                                      onAvfallChange={handleAvfallChange}
                                      onDeleteRow={handleDeleteRow}
                                  />
                              ))
                            : displayAvfall.map((avfall, index) => (
                                  <ProsjektAvfallDisplayRow
                                      key={`${avfall.id}-${index}`}
                                      avfall={avfall}
                                      totalMengde={totalMengde}
                                  />
                              ))}
                        {isEditMode && (
                            <Grid
                                container={true}
                                className={classes.addButtonContainer}
                            >
                                <Grid item={true} xs={1} />
                                <Grid item={true}>
                                    <ActionButton
                                        variant="outlined"
                                        color="secondary"
                                        onClick={handleAddRow}
                                        actionType="button"
                                        startIcon={
                                            <Illustration
                                                icon={IconName.PLUS}
                                            />
                                        }
                                        translation="sustainability.prosjektAvfall.addRow"
                                    />
                                </Grid>
                            </Grid>
                        )}
                    </Grid>
                    <ProsjektAvfallSummary
                        isEditMode={isEditMode}
                        totalMengde={totalMengde}
                        totalMengdeSortert={totalMengdeSortert}
                        totalMengdeUsortert={totalMengdeUsortert}
                    />
                </Grid>
                <ConfirmationDialog
                    anchorRef={anchorRef}
                    showDialog={showConfirmationDialog}
                    dialogContent={t(
                        'sustainability.prosjektAvfall.analyzeWarning',
                    )}
                    onCancel={() => setShowConfirmationDialog(false)}
                    onConfirm={handleAnalyze}
                    className="withBlur"
                />
            </Grid>
        </Grid>
    );
}
